///
/// EarthBrowser
///
/// Created by Matt Giger on 1/13/2025
/// Copyright (c) 2025 Geodata Labs
///

import { authenticatedApi } from "@/redux/API/authenticatedApi"
import type { BuoyEntry, BuoyHistoryParams, BuoyHistoryResponse, BuoyRequestParams } from "@shared-types/buoy"
import type { CloudEntry, CloudRequestParams, EarthquakeEntry, EarthquakeRequestParams, WildfireEntry, WildfireRequestParams } from "@shared-types/data"
import type { FeatureCollection, Point } from "@shared-types/geojson"
import type { NexradAllStationsParams, NexradStation, NexradStationHistory, NexradStationHistoryParams } from "@shared-types/nexrad"

export const earthBrowserApi = authenticatedApi.injectEndpoints({
	endpoints: (builder) => ({
		getEarthquakes: builder.query<FeatureCollection<Point, EarthquakeEntry>, EarthquakeRequestParams>({
			query: (params) => ({
				url: "/api/quakes",
				params: params,
			}),
			providesTags: ["Earthquakes"],
		}),
		getCloudHistory: builder.query<FeatureCollection<null, CloudEntry>, CloudRequestParams>({
			query: (params) => ({
				url: "/api/clouds",
				params: params,
			}),
			providesTags: ["Clouds"],
		}),
		getWildfireHistory: builder.query<FeatureCollection<null, WildfireEntry>, WildfireRequestParams>({
			query: (params) => ({
				url: "/api/wildfires",
				params: params,
			}),
			providesTags: ["Wildfires"],
		}),
		getBuoys: builder.query<FeatureCollection<Point, BuoyEntry>, BuoyRequestParams>({
			query: (params) => ({
				url: "/api/buoys",
				params: params,
			}),
			providesTags: ["Buoys"],
		}),
		getBuoyHistory: builder.query<BuoyHistoryResponse, BuoyHistoryParams>({
			query: (params) => ({
				url: "/api/buoys",
				params: {
					stationId: params.stationId,
					hours: params.hours ?? 24,
				},
			}),
			providesTags: ["BuoyHistory"],
		}),
		// NEXRAD radar data - all stations with scan history
		getNexradAllStations: builder.query<NexradStationHistory[], NexradAllStationsParams>({
			query: (params) => ({
				url: "/api/nexrad",
				params: params,
			}),
			providesTags: ["Nexrad"],
		}),
		// NEXRAD radar data - single station with scan history
		getNexradStationHistory: builder.query<NexradStationHistory, NexradStationHistoryParams>({
			query: (params) => ({
				url: `/api/nexrad/${params.stationId}/history`,
				params: { hours: params.hours },
			}),
			providesTags: ["NexradHistory"],
		}),
		// NEXRAD station list (no scans, just metadata)
		getNexradStationList: builder.query<FeatureCollection<Point, NexradStation>, void>({
			query: () => ({
				url: "/api/nexrad/stations",
			}),
			providesTags: ["NexradStations"],
		}),
	}),
})

export const {
	useGetEarthquakesQuery,
	useGetCloudHistoryQuery,
	useGetWildfireHistoryQuery,
	useGetBuoysQuery,
	useGetBuoyHistoryQuery,
	useGetNexradAllStationsQuery,
	useGetNexradStationHistoryQuery,
	useGetNexradStationListQuery,
} = earthBrowserApi
